/**
 * PricingCard — Multi-row selling price card for the item detail page.
 *
 * Shows one row per Price List affecting this item. Primary (storefront)
 * row is highlighted with blue background. Each row shows base price,
 * customer-pays, and the applied pricing rule.
 *
 * Part of Plan 170 (Item Pricing Table Redesign), Epic 02.
 */

import { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { formatPrice } from "@/lib/format/currency";
import { toast } from "sonner";
import { Loader2, RotateCcw } from "lucide-react";
import {
  useItemPricingTable,
  useItemPricingSummary,
  usePricingCustomerGroups,
} from "@/features/inventory/api/pricingSummaryHooks";
import { useUpdateItemPrice } from "@/features/inventory/api/useUpdateItemPrice";
import { UpdatePriceModal } from "./UpdatePriceModal";
import { RunASaleModal } from "./RunASaleModal";
import { VolumePricingSection } from "./VolumePricingSection";
import { useProductTiers } from "@/features/storefront/promotions/api/promotionTierHooks";
import { PricingRow } from "./PricingRow";
import { AddPriceListModal } from "./AddPriceListModal";
import { PromotionPill, formatDiscount, formatShortDate } from "./PromotionPill";
import { usePageTranslation } from "@/i18n/useTranslation";
import { inventoryTranslations as enInventory } from "@/i18n/locales/en/inventory";
import { inventoryTranslations as thInventory } from "@/i18n/locales/th/inventory";
import { useToggleShowOnDetail, useToggleStorefrontEnabled, useTogglePromotionDisabled } from "@/features/inventory/api/useStorefrontToggles";
import { useEndPromotion } from "@/features/inventory/api/useEndPromotion";
import type { Promotion } from "@/features/inventory/api/pricingSummaryHooks";
import { CARD } from "@/config/design-tokens";
import type { ShopifySectionWidgetProps } from "../types";

// ── Skeleton Loading ─────────────────────────────────────────

function PricingCardSkeleton() {
  return (
    <div className={cn(CARD, "p-6 space-y-5")}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <Skeleton className="h-3 w-24" />
        <Skeleton className="h-8 w-20 rounded-full" />
      </div>
      {/* Row 1 */}
      <div className="space-y-2 pt-2">
        <Skeleton className="h-3 w-32" />
        <Skeleton className="h-5 w-24" />
        <Skeleton className="h-6 w-28" />
        <Skeleton className="h-3 w-40" />
      </div>
      <div className="border-t border-border/40" />
      {/* Row 2 */}
      <div className="space-y-2">
        <Skeleton className="h-3 w-28" />
        <Skeleton className="h-5 w-20" />
        <Skeleton className="h-6 w-24" />
      </div>
    </div>
  );
}

// ── Error State ──────────────────────────────────────────────

function PricingCardError({ onRetry, errorLabel, retryLabel }: { onRetry: () => void; errorLabel: string; retryLabel: string }) {
  return (
    <div className={cn(CARD, "p-6")}>
      <div className="flex flex-col items-center gap-3 py-4">
        <p className="text-sm text-muted-foreground">
          {errorLabel}
        </p>
        <Button
          variant="ghost"
          size="sm"
          onClick={onRetry}
          className="gap-1.5 text-xs"
        >
          <RotateCcw className="h-3 w-3" />
          {retryLabel}
        </Button>
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────

const PRICING_AUTOOPEN_KEY = "openPricingAfterCreate";

export function PricingCard({ formData, onSave, isNew }: ShopifySectionWidgetProps) {
  const t = usePageTranslation({ en: enInventory, th: thInventory });
  const itemCode = (formData?.name as string) || "";
  const itemName = (formData?.item_name as string) || "";
  const [selectedGroup, setSelectedGroup] = useState<string>("All Customer Groups");
  const [isUpdatePriceOpen, setIsUpdatePriceOpen] = useState(false);
  const [isRunASaleOpen, setIsRunASaleOpen] = useState(false);
  const [isAddPriceListOpen, setIsAddPriceListOpen] = useState(false);
  const [isAutoSaving, setIsAutoSaving] = useState(false);

  // ── Data Fetching ──────────────────────────────────────────

  // New multi-row pricing table (Plan 170)
  const tableQuery = useItemPricingTable(
    itemCode,
    selectedGroup !== "All Customer Groups" ? selectedGroup : undefined
  );

  // Old pricing summary — ONLY needed for RunASaleModal (promotions, has_active_promotion).
  // TODO: Migrate RunASaleModal to use new API, then remove this hook.
  const pricingQuery = useItemPricingSummary(
    itemCode,
    selectedGroup !== "All Customer Groups" ? selectedGroup : undefined
  );

  const groupsQuery = usePricingCustomerGroups();

  // Promotion Tier Template link (Plan 199)
  const productTiersQuery = useProductTiers(itemCode);
  const linkedTemplate = productTiersQuery.data;

  // ── Storefront toggle mutations ──────────────────────────────
  const toggleShowMutation = useToggleShowOnDetail(itemCode);
  const toggleEnabledMutation = useToggleStorefrontEnabled(itemCode);
  const toggleDisabledMutation = useTogglePromotionDisabled(itemCode);
  const endPromotionMutation = useEndPromotion(itemCode);
  const [endingRuleName, setEndingRuleName] = useState<string | null>(null);
  const [editingPromotion, setEditingPromotion] = useState<Promotion | undefined>(undefined);

  // Primary row from the new API
  const primaryRow = tableQuery.data?.rows?.[0] ?? null;
  const rows = tableQuery.data?.rows ?? [];
  const existingPriceLists = useMemo(() => rows.map((r) => r.price_list), [rows]);

  // Derived state for RunASaleModal (from old API)
  const oldData = pricingQuery.data;
  const hasActivePromotion = oldData?.has_active_promotion ?? false;
  const promotions = oldData?.promotions ?? [];
  const primaryCurrency = primaryRow?.currency ?? oldData?.currency ?? "THB";

  const activePromotionLabel = useMemo(() => {
    const active = promotions.find((p) => p.status === "Active");
    if (!active) return null;
    const discount = formatDiscount(active);
    const endDate = active.valid_upto ? ` until ${formatShortDate(active.valid_upto)}` : "";
    return `${discount} ${active.title}${endDate}`.trim();
  }, [promotions]);

  // ── Auto-open pricing modal after create-and-navigate ──────

  useEffect(() => {
    if (itemCode && !isNew && sessionStorage.getItem(PRICING_AUTOOPEN_KEY)) {
      sessionStorage.removeItem(PRICING_AUTOOPEN_KEY);
      setIsUpdatePriceOpen(true);
    }
  }, [itemCode, isNew]);

  // ── Undo support for price updates ─────────────────────────

  const previousRateRef = useRef<number | null>(null);
  const variantOf = (formData?.variant_of as string) || undefined;
  const updatePriceMutation = useUpdateItemPrice(itemCode, variantOf);

  const handleUpdateSuccess = useCallback(
    (response: { previous_rate: number | null; new_rate: number }) => {
      previousRateRef.current = response.previous_rate;
      const formattedPrice = formatPrice(response.new_rate, primaryCurrency);

      toast.success(`${t.pricingUpdatedTo} ${formattedPrice}`, {
        duration: 4000,
        action: response.previous_rate != null
          ? {
              label: t.pricingUndo,
              onClick: () => {
                const prevRate = previousRateRef.current;
                if (prevRate == null) return;
                updatePriceMutation.mutate(prevRate, {
                  onSuccess: () => {
                    toast.success(`${t.pricingRevertedTo} ${formatPrice(prevRate, primaryCurrency)}`);
                  },
                  onError: () => {
                    toast.error(t.pricingUndoFailed);
                  },
                });
              },
            }
          : undefined,
      });
    },
    [primaryCurrency, updatePriceMutation]
  );

  const handleRunASaleClose = useCallback((open: boolean) => {
    setIsRunASaleOpen(open);
    if (!open) setEditingPromotion(undefined);
  }, []);

  const handleEditPromotion = useCallback((promo: Promotion) => {
    setEditingPromotion(promo);
    setIsRunASaleOpen(true);
  }, []);

  // ── Promotion pill callbacks ─────────────────────────────────

  const handleToggleShowOnDetail = useCallback(
    (promo: Promotion) => {
      toggleShowMutation.mutate({ ruleName: promo.name, itemCode });
    },
    [toggleShowMutation, itemCode]
  );

  const handleToggleStorefrontEnabled = useCallback(
    (promo: Promotion) => {
      toggleEnabledMutation.mutate({ ruleName: promo.name });
    },
    [toggleEnabledMutation]
  );

  const handleToggleDisabled = useCallback(
    (promo: Promotion) => {
      toggleDisabledMutation.mutate({ ruleName: promo.name });
    },
    [toggleDisabledMutation]
  );

  const handleEndPromotion = useCallback((promo: Promotion) => {
    setEndingRuleName(promo.name);
  }, []);

  const handleConfirmEnd = useCallback(
    (promo: Promotion) => {
      endPromotionMutation.mutate(promo.name, {
        onSuccess: () => {
          toast.success(t.pricingPromoEnded);
          setEndingRuleName(null);
        },
        onError: () => {
          toast.error(t.pricingPromoEndFailed);
          setEndingRuleName(null);
        },
      });
    },
    [endPromotionMutation]
  );

  const handleCancelEnd = useCallback(() => {
    setEndingRuleName(null);
  }, []);

  // ── Bookable item: pricing handled in BookingSection ───────
  if (formData?.is_bookable === 1 || formData?.is_bookable === true) return null;

  // ── Template item: no pricing on templates ─────────────────
  // Guard: only show template message if has_variants AND actually has attributes.
  // Items with has_variants=1 but no attributes are orphan templates (stale checkbox).
  const attributes = formData?.attributes;
  const hasRealVariants =
    (formData?.has_variants === 1 || formData?.has_variants === true) &&
    Array.isArray(attributes) &&
    attributes.length > 0;

  if (hasRealVariants) {
    return (
      <div className={cn(CARD, "p-6")}>
        <p className="text-xs font-medium text-muted-foreground/70 uppercase tracking-widest mb-3">
          {t.pricingSellingPrice}
        </p>
        <p className="text-sm text-muted-foreground text-center py-4">
          {t.pricingTemplateMessage}
        </p>
      </div>
    );
  }

  // ── New item: "Set selling price" CTA ──────────────────────

  if (isNew) {
    return (
      <div className={CARD}>
        <div className="py-8 text-center px-4">
          <p className="text-sm font-medium text-foreground mb-1">
            {t.pricingHowMuch}
          </p>
          <p className="text-xs text-muted-foreground mb-4">
            {t.pricingStorefrontNote}
          </p>
          <Button
            onClick={async () => {
              if (onSave) {
                if (!formData?.stock_uom) {
                  toast.error(t.pricingSelectUnitFirst, {
                    description: t.pricingUnitRequired,
                  });
                  return;
                }
                setIsAutoSaving(true);
                try {
                  sessionStorage.setItem(PRICING_AUTOOPEN_KEY, "1");
                  await onSave();
                } catch (err) {
                  sessionStorage.removeItem(PRICING_AUTOOPEN_KEY);
                  const detail = err instanceof Error ? err.message : t.pricingFillRequired;
                  toast.error(t.pricingCouldNotSave, { description: detail });
                } finally {
                  setIsAutoSaving(false);
                }
              }
            }}
            disabled={isAutoSaving}
            className="rounded-full min-h-[44px] px-8 bg-primary text-primary-foreground"
          >
            {isAutoSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t.pricingSavingItem}
              </>
            ) : (
              t.pricingSetSellingPrice
            )}
          </Button>
        </div>
      </div>
    );
  }

  // ── Modals ─────────────────────────────────────────────────

  const modals = (
    <>
      <UpdatePriceModal
        open={isUpdatePriceOpen}
        onOpenChange={setIsUpdatePriceOpen}
        itemCode={itemCode}
        itemName={itemName}
        currentBasePrice={primaryRow?.base_price ?? oldData?.base_price ?? null}
        currency={primaryCurrency}
        priceList={primaryRow?.price_list ?? oldData?.price_list ?? ""}
        hasActivePromotion={hasActivePromotion}
        activePromotionLabel={activePromotionLabel}
        onSuccess={handleUpdateSuccess}
        variantOf={variantOf}
      />
      <RunASaleModal
        open={isRunASaleOpen}
        onOpenChange={handleRunASaleClose}
        itemCode={itemCode}
        itemName={itemName}
        basePrice={primaryRow?.base_price ?? oldData?.base_price ?? null}
        currency={primaryCurrency}
        editPromotion={editingPromotion}
      />
      <AddPriceListModal
        open={isAddPriceListOpen}
        onOpenChange={setIsAddPriceListOpen}
        itemCode={itemCode}
        stockUom={(formData?.stock_uom as string) || ""}
        existingPriceLists={existingPriceLists}
      />
    </>
  );

  // ── Loading skeleton ───────────────────────────────────────

  if (tableQuery.isLoading && !tableQuery.data) {
    return <>{modals}<PricingCardSkeleton /></>;
  }

  // ── Error state ────────────────────────────────────────────

  if (tableQuery.isError && !tableQuery.data) {
    return (
      <>
        {modals}
        <PricingCardError onRetry={() => tableQuery.refetch()} errorLabel={t.pricingCouldNotLoad} retryLabel={t.pricingRetry} />
      </>
    );
  }

  // ── Empty state: no price lists at all ─────────────────────

  if (rows.length === 0) {
    return (
      <>
        {modals}
        <div className={cn(CARD, "p-6")}>
          <p className="text-xs font-medium text-muted-foreground/70 uppercase tracking-widest mb-3">
            {t.pricingSellingPrice}
          </p>
          <div className="text-center py-6">
            <p className="text-sm font-medium text-foreground mb-1">
              {t.pricingNoPricing}
            </p>
            <p className="text-xs text-muted-foreground mb-4">
              {t.pricingAddPriceListToStart}
            </p>
            <Button
              onClick={() => setIsAddPriceListOpen(true)}
              className="rounded-full min-h-[44px] px-8"
            >
              {t.pricingSetSellingPrice}
            </Button>
          </div>
        </div>
      </>
    );
  }

  const isFetching = tableQuery.isFetching && !!tableQuery.data;

  return (
    <>
      {modals}
      <div className="space-y-2">
        {/* ── Selling Price Card ─────────────────────────────── */}
        <div
          role="region"
          aria-labelledby="pricing-card-title"
          className={cn(
            CARD,
            "transition-opacity duration-150 ease-out",
            isFetching ? "opacity-60" : "opacity-100"
          )}
        >
          {/* Card Header */}
          <div className="flex items-center justify-between px-5 pt-5 pb-3">
            <p
              id="pricing-card-title"
              className="text-xs font-medium text-muted-foreground/70 uppercase tracking-widest"
            >
              {t.pricingSellingPrice}
            </p>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsRunASaleOpen(true)}
              className="text-xs text-muted-foreground hover:text-foreground h-7 px-2"
            >
              {t.pricingRunASale}
            </Button>
          </div>

          <div className="border-t border-border/40 mx-5" />

          {/* Price List Rows */}
          {rows.map((row, idx) => (
            <div key={row.price_list}>
              <PricingRow
                row={row}
                onEditBase={
                  row.is_primary
                    ? () => setIsUpdatePriceOpen(true)
                    : undefined
                }
              />
              {idx < rows.length - 1 && (
                <div className="border-t border-border/40 mx-5" />
              )}
            </div>
          ))}

          {/* ── Active Promotions ──────────────────────────── */}
          {promotions.length > 0 && (
            <>
              <div className="border-t border-border/40 mx-5" />
              <div className="px-5 py-3">
                <p className="text-xs font-medium text-muted-foreground/70 uppercase tracking-widest mb-2">
                  {t.promoPromotions}
                </p>
                <div className="space-y-1.5">
                  {promotions.map((promo) => (
                    <PromotionPill
                      key={promo.name}
                      promo={promo}
                      isEnding={endingRuleName === promo.name}
                      onEdit={handleEditPromotion}
                      onToggleShowOnDetail={handleToggleShowOnDetail}
                      onToggleStorefrontEnabled={handleToggleStorefrontEnabled}
                      onToggleDisabled={handleToggleDisabled}
                      onEnd={handleEndPromotion}
                      onConfirmEnd={handleConfirmEnd}
                      onCancelEnd={handleCancelEnd}
                    />
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Footer: Add price list */}
          <div className="border-t border-border/40 mx-5" />
          <div className="px-5 py-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setIsAddPriceListOpen(true)}
              className="text-xs text-sky-600 hover:text-sky-700 h-7 px-0"
            >
              {t.pricingAddPriceList}
            </Button>
          </div>

          {/* Preview as — customer group context switcher */}
          <div className="flex items-center justify-center pb-4">
            <Select value={selectedGroup} onValueChange={setSelectedGroup}>
              <SelectTrigger
                className="h-8 w-auto gap-1.5 border-0 bg-transparent text-xs text-muted-foreground hover:text-foreground px-2"
                aria-label="Preview pricing as different customer group"
              >
                <span className="text-muted-foreground/60">{t.pricingPreviewAs}</span>
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="center">
                {(groupsQuery.data ?? [{ name: "All Customer Groups", default_price_list: "" }]).map(
                  (group) => (
                    <SelectItem key={group.name} value={group.name}>
                      {group.name}
                    </SelectItem>
                  )
                )}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* ── Promotion Tier Template chip (Plan 199) ───────��� */}
        {linkedTemplate && (
          <div className={cn(CARD, "px-4 py-3")}>
            <a
              href={`/storefront/promotions/${encodeURIComponent(linkedTemplate.template_name)}`}
              onClick={(e) => {
                e.preventDefault();
                // Use React Router navigation if available
                window.location.href = `/storefront/promotions/${encodeURIComponent(linkedTemplate.template_name)}`;
              }}
              className="flex items-center gap-2 text-sm text-primary hover:underline"
            >
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-primary/10 text-xs font-medium">
                Promotion: {linkedTemplate.template_name}
              </span>
            </a>
          </div>
        )}

        {/* ── Volume Pricing (relocated below main card) ──────── */}
        {primaryRow?.has_item_price && !linkedTemplate && (
          <div className={cn(CARD, "px-4")}>
            <VolumePricingSection
              itemCode={itemCode}
              basePrice={primaryRow.base_price}
              currency={primaryCurrency}
            />
          </div>
        )}
        {primaryRow?.has_item_price && linkedTemplate && (
          <div className={cn(CARD, "px-4 py-3")}>
            <p className="text-xs text-muted-foreground">
              Volume pricing managed by promotion template "{linkedTemplate.template_name}"
            </p>
          </div>
        )}
      </div>
    </>
  );
}
